/**
 * Pack Config Helpers - Resolution logic for MuxVideoPlayerWithCTA
 * 
 * Provides functions to resolve playbackId, ctaUrl, ctaLabel, and pricing
 * from API data with multiple fallback levels:
 * 1. API data (priceByCountry[country])
 * 2. Static configuration (telegramGroupsVideo, stripe)
 * 3. Hardcoded defaults
 */

import type { PackConfig, PackPriceByCountry } from "./api";

/**
 * Get user's language from browser (es or en)
 */
export function getUserLanguage(): "es" | "en" {
  const lang = navigator.language.toLowerCase();
  return lang.startsWith("es") ? "es" : "en";
}

/**
 * Get country-specific configuration from pack.
 * - If country is in priceByCountry and not explicitly inactive: returns that config.
 * - If country is explicitly inactive (isActive: false): returns config with isActive: false.
 * - If country is not in priceByCountry (but pack has priceByCountry): returns a fallback
 *   config with pack default amount/currency, isActive: true, and no playbackId/ctaUrl so
 *   resolvers use pack.fallbackPlaybackId and static fallbacks.
 * - If pack is null or has no priceByCountry: returns null.
 */
export function getCountryConfig(
  pack: PackConfig | null | undefined,
  countryCode: string
): PackPriceByCountry | null {
  if (!pack) return null;
  const config = pack.priceByCountry?.[countryCode];

  // Explicit soft delete: return config with isActive: false so callers treat as inactive
  if (config && config.isActive === false) return { ...config, isActive: false };

  // Country in API: use its config (may omit playbackId/ctaUrl; resolvers use fallbacks)
  if (config) return config;

  // Country not in API: return fallback so resolvers use pack defaults + static fallbacks
  if (pack.priceByCountry) {
    return {
      amount: pack.amount ?? 0,
      currency: pack.currency ?? "usd",
      isActive: true,
    };
  }

  return null;
}

/**
 * Resolve playback ID with fallback chain:
 * 1. priceByCountry[country].playbackId (API)
 * 2. pack.fallbackPlaybackId (API)
 * 3. staticFallback(country) (telegramGroupsVideo)
 * 4. hardcodedFallback (env var)
 */
export function resolvePlaybackId(
  pack: PackConfig | null | undefined,
  countryCode: string,
  options: { 
    staticFallback?: (cc: string) => string | null; 
    hardcodedFallback?: string 
  } = {}
): string {
  const countryConfig = getCountryConfig(pack, countryCode);
  if (countryConfig?.playbackId) return countryConfig.playbackId;
  if (pack?.fallbackPlaybackId) return pack.fallbackPlaybackId;
  if (options.staticFallback) {
    const id = options.staticFallback(countryCode);
    if (id) return id;
  }
  return options.hardcodedFallback || "";
}

/**
 * Resolve CTA URL with fallback chain:
 * 1. priceByCountry[country].ctaUrl (API)
 * 2. staticFallback(country, pack) (telegramGroupsVideo)
 * 3. hardcodedFallback
 * 4. Generic checkout URL with pack info
 */
export function resolveCtaUrl(
  pack: PackConfig | null | undefined,
  countryCode: string,
  options: { 
    staticFallback?: (cc: string, p?: PackConfig) => string | null; 
    hardcodedFallback?: string 
  } = {}
): string {
  const countryConfig = getCountryConfig(pack, countryCode);
  if (countryConfig?.ctaUrl) return countryConfig.ctaUrl;
  if (options.staticFallback && pack) {
    const url = options.staticFallback(countryCode, pack);
    if (url) return url;
  }
  if (options.hardcodedFallback) return options.hardcodedFallback;
  if (pack) {
    const p = countryConfig || { amount: pack.amount, currency: pack.currency };
    return `/checkout?pack=${pack.id}&country=${countryCode}&amount=${p.amount}&currency=${p.currency}`;
  }
  return "#";
}

/**
 * Resolve CTA label with fallback chain:
 * 1. priceByCountry[country].ctaLabelEs/En (API)
 * 2. staticFallback(language) (videoCTAConfig)
 * 3. hardcodedFallbackEs/En
 */
export function resolveCtaLabel(
  pack: PackConfig | null | undefined,
  countryCode: string,
  language: "es" | "en",
  options: { 
    staticFallback?: (lang: "es" | "en") => string | null; 
    hardcodedFallbackEs?: string; 
    hardcodedFallbackEn?: string 
  } = {}
): string {
  const countryConfig = getCountryConfig(pack, countryCode);
  if (language === "es" && countryConfig?.ctaLabelEs) return countryConfig.ctaLabelEs;
  if (language === "en" && countryConfig?.ctaLabelEn) return countryConfig.ctaLabelEn;
  if (options.staticFallback) {
    const label = options.staticFallback(language);
    if (label) return label;
  }
  return language === "es" 
    ? (options.hardcodedFallbackEs || "Unirte") 
    : (options.hardcodedFallbackEn || "Join");
}

/**
 * Resolve pricing (amount + currency) with fallback:
 * 1. priceByCountry[country] (API)
 * 2. pack.amount + pack.currency (API default)
 * 3. { amount: 0, currency: "usd" }
 */
export function resolvePricing(
  pack: PackConfig | null | undefined,
  countryCode: string
): { amount: number; currency: string } {
  const countryConfig = getCountryConfig(pack, countryCode);
  if (countryConfig) return { amount: countryConfig.amount, currency: countryConfig.currency };
  return { amount: pack?.amount || 0, currency: pack?.currency || "usd" };
}

/**
 * Format price for display (e.g. "$29.99")
 */
export function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { 
    style: "currency", 
    currency: currency.toUpperCase() 
  }).format(amount / 100);
}

/**
 * Complete configuration for video player
 */
export interface PackConfigForVideo {
  playbackId: string;
  fallbackPlaybackId: string | null;
  ctaUrl: string;
  ctaLabel: string;
  amount: number;
  currency: string;
  priceFormatted: string;
  isActive: boolean;
}

/**
 * Resolve all configuration for MuxVideoPlayerWithCTA
 * 
 * This is a pure function (not a React hook despite the name).
 * The name follows the convention but could be renamed to `resolvePackConfigForVideo`.
 * 
 * @param pack - Pack configuration from API (or null if API failed)
 * @param countryCode - User's country code (e.g. "AR", "US")
 * @param language - User's language ("es" or "en")
 * @param options - Fallback functions and hardcoded values
 */
export function usePackConfigForVideo(
  pack: PackConfig | null | undefined,
  countryCode: string,
  language: "es" | "en",
  options: {
    playbackIdFallback?: (cc: string) => string | null;
    playbackIdHardcoded?: string;
    ctaUrlFallback?: (cc: string, p?: PackConfig) => string | null;
    ctaUrlHardcoded?: string;
    ctaLabelFallback?: (lang: "es" | "en") => string | null;
    ctaLabelHardcodedEs?: string;
    ctaLabelHardcodedEn?: string;
  } = {}
): PackConfigForVideo {
  const countryConfig = getCountryConfig(pack, countryCode);
  // Active when: country not in API (use fallback) OR API did not set isActive: false (explicit soft delete)
  const isActive = countryConfig === null || countryConfig.isActive !== false;
  const pricing = resolvePricing(pack, countryCode);

  return {
    playbackId: resolvePlaybackId(pack, countryCode, {
      staticFallback: options.playbackIdFallback,
      hardcodedFallback: options.playbackIdHardcoded,
    }),
    fallbackPlaybackId: pack?.fallbackPlaybackId || null,
    ctaUrl: resolveCtaUrl(pack, countryCode, {
      staticFallback: options.ctaUrlFallback,
      hardcodedFallback: options.ctaUrlHardcoded,
    }),
    ctaLabel: resolveCtaLabel(pack, countryCode, language, {
      staticFallback: options.ctaLabelFallback,
      hardcodedFallbackEs: options.ctaLabelHardcodedEs,
      hardcodedFallbackEn: options.ctaLabelHardcodedEn,
    }),
    amount: pricing.amount,
    currency: pricing.currency,
    priceFormatted: formatPrice(pricing.amount, pricing.currency),
    isActive,
  };
}
